import { AriApiQueryCreatorFetch } from '../AriApiCreator';
import { prismScopes } from '../auth';
import { prismInvestmentService } from '../AIMApi/controllers/hooksServices/prismInvestmentApi';
// Need to import this type for the getUserRolesAdmin endpoint
import type { UserRoleDetailVm } from './AdminModels';
import {
  AddReportRecipientVm,
  AdminUserPermissionVm,
  AppUserVm,
  AppVm,
  AssignUserToRoleVm,
  CoreAppUserVm,
  CreateDataScopeVm,
  CreatePermissionVm,
  CreateRoleVm,
  DataScopeDetailVm,
  DataScopeListItemVm,
  DataScopeUserVm,
  DurationTrendVm,
  EffectivePropertiesVm,
  FindOrCreateUserVm,
  PermissionUserVm,
  PermissionVm,
  PreviewRequestVm,
  PreviewResultVm,
  ProtectedFieldGroupVm,
  ReplaceRulesVm,
  ReportRecipientItem,
  ReportTypeVm,
  RolePermissionVm,
  RoleUserVm,
  RoleVm,
  ScopeAppVm,
  ScopeDimensionLookupsVm,
  SyncResultVm,
  UpdateDataScopeVm,
  UpdatePermissionVm,
  UpdateReportRecipientVm,
  UpdateRoleVm,
  UsageLogFilterOptionsVm,
  UsageLogFilters,
  UsageLogMetricsVm,
  UsageLogPagedResponse,
  UserDataFlowVm,
  UserDataScopeDiagnosticsVm,
  UserDataScopeVm
} from './AdminModels';
import getPrismBaseUrl from '../AIMApi/controllers/utils/getBaseUrl';

const url = getPrismBaseUrl();

/** After admin mutations that may affect the current user's permissions, refetch /user/security */
const invalidateUserSecurity = async (_: unknown, { dispatch, queryFulfilled }: { dispatch: any; queryFulfilled: Promise<any> }) => {
  try { await queryFulfilled; } catch { return; }
  dispatch(prismInvestmentService.hooks.util.invalidateTags(['UserSecurity']));
};

/**
 * An RTK Query enhanced API builder for admin operations, providing CRUD endpoints for apps, users, roles, and permissions backed by the PrismCore database.
 * <note>Every mutation that affects the current user's permissions or roles automatically invalidates the `UserSecurity` tag, triggering a refetch of `/user/security` via the shared `invalidateUserSecurity` effect.</note>
 */
const AdminApi = AriApiQueryCreatorFetch({
  scopesObj: prismScopes,
  serviceURL: url,
  apiOptions: {
    reducerPath: 'adminApi',
    keepUnusedDataFor: 300,
    tagTypes: ['App', 'Role', 'Permission', 'User', 'RoleUser', 'UsageLog', 'ReportType', 'ReportRecipient', 'DataScope', 'FieldGroup'],
    endpoints: (builder) => ({

      // ── Apps ───────────────────────────────────────────────────────────────
      getAdminApps: builder.query<AppVm[], void>({
        query: () => '/app-admin/apps',
        providesTags: ['App']
      }),
      getAppUsers: builder.query<AppUserVm[], number>({
        query: (appId) => `/app-admin/apps/${appId}/users`,
        providesTags: (_r, _e, appId) => [{ type: 'User', id: `app-${appId}` }, 'RoleUser']
      }),

      // ── Roles ──────────────────────────────────────────────────────────────
      getAdminRoles: builder.query<RoleVm[], void>({
        query: () => '/app-admin/roles',
        providesTags: ['Role']
      }),
      createRole: builder.mutation<{ id: number; roleName: string; displayName: string; appId: number }, CreateRoleVm>({
        query: (body) => ({ url: '/app-admin/roles', method: 'POST', body }),
        invalidatesTags: ['Role']
      }),
      updateRole: builder.mutation<RoleVm, { id: number; data: UpdateRoleVm }>({
        query: ({ id, data }) => ({ url: `/app-admin/roles/${id}`, method: 'PUT', body: data }),
        invalidatesTags: ['Role'],
        onQueryStarted: invalidateUserSecurity
      }),
      deleteRole: builder.mutation<void, number>({
        query: (id) => ({ url: `/app-admin/roles/${id}`, method: 'DELETE' }),
        invalidatesTags: ['Role'],
        onQueryStarted: invalidateUserSecurity
      }),
      cloneRole: builder.mutation<{ id: number; roleName: string; displayName: string; permissionsCopied: number }, { roleId: number; data: { roleName: string; displayName?: string; description?: string } }>({
        query: ({ roleId, data }) => ({ url: `/app-admin/roles/${roleId}/clone`, method: 'POST', body: data }),
        invalidatesTags: ['Role', 'Permission']
      }),
      copyPermissionsFromRole: builder.mutation<{ permissionsAdded: number; sourceRoleId: number }, { roleId: number; sourceRoleId: number }>({
        query: ({ roleId, sourceRoleId }) => ({ url: `/app-admin/roles/${roleId}/copy-permissions`, method: 'POST', body: { sourceRoleId } }),
        invalidatesTags: (_r, _e, { roleId }) => [{ type: 'Permission', id: roleId }, 'Role'],
        onQueryStarted: invalidateUserSecurity
      }),

      // ── Role Permissions ───────────────────────────────────────────────────
      getRolePermissions: builder.query<RolePermissionVm[], number>({
        query: (roleId) => `/app-admin/roles/${roleId}/permissions`,
        providesTags: (_r, _e, roleId) => [{ type: 'Permission', id: roleId }]
      }),
      assignPermissionToRole: builder.mutation<{ roleId: number; permissionId: number }, { roleId: number; permissionId: number }>({
        query: ({ roleId, permissionId }) => ({
          url: `/app-admin/roles/${roleId}/permissions`,
          method: 'POST',
          body: { permissionId }
        }),
        invalidatesTags: (_r, _e, { roleId }) => [{ type: 'Permission', id: roleId }],
        onQueryStarted: invalidateUserSecurity
      }),
      removePermissionFromRole: builder.mutation<void, { roleId: number; permissionId: number }>({
        query: ({ roleId, permissionId }) => ({
          url: `/app-admin/roles/${roleId}/permissions/${permissionId}`,
          method: 'DELETE'
        }),
        invalidatesTags: (_r, _e, { roleId }) => [{ type: 'Permission', id: roleId }],
        onQueryStarted: invalidateUserSecurity
      }),
      toggleRolePermission: builder.mutation<{ roleId: number; permissionId: number; isActive: boolean }, { roleId: number; permissionId: number }>({
        query: ({ roleId, permissionId }) => ({
          url: `/app-admin/roles/${roleId}/permissions/${permissionId}`,
          method: 'PATCH'
        }),
        invalidatesTags: (_r, _e, { roleId }) => [{ type: 'Permission', id: roleId }, 'Role'],
        onQueryStarted: invalidateUserSecurity
      }),

      // ── Permissions (all) ──────────────────────────────────────────────────
      getAdminPermissions: builder.query<PermissionVm[], void>({
        query: () => '/app-admin/permissions',
        providesTags: ['Permission']
      }),
      createPermission: builder.mutation<PermissionVm, CreatePermissionVm>({
        query: (body) => ({ url: '/app-admin/permissions', method: 'POST', body }),
        invalidatesTags: ['Permission', 'App']
      }),
      updatePermission: builder.mutation<PermissionVm, { id: number; data: UpdatePermissionVm }>({
        query: ({ id, data }) => ({ url: `/app-admin/permissions/${id}`, method: 'PATCH', body: data }),
        invalidatesTags: ['Permission', 'App'],
        onQueryStarted: invalidateUserSecurity
      }),
      getPermissionUsers: builder.query<PermissionUserVm[], number>({
        query: (permissionId) => `/app-admin/permissions/${permissionId}/users`,
        providesTags: (_r, _e, permissionId) => [{ type: 'Permission', id: `users-${permissionId}` }]
      }),
      // Toggles every AppUserRole assignment for this user across all roles that actively
      // grant this permission (not just a single role/user pair). Unrelated role assignments
      // for the user are left untouched.
      togglePermissionUserRoles: builder.mutation<
        { permissionId: number; appUserId: number; isActive: boolean; affectedRoleIds: number[]; affectedCount: number },
        { permissionId: number; userId: number; isActive: boolean }
      >({
        query: ({ permissionId, userId, isActive }) => ({
          url: `/app-admin/permissions/${permissionId}/users/${userId}/toggle`,
          method: 'PATCH',
          body: { isActive }
        }),
        invalidatesTags: (_r, _e, { permissionId }) => [
          { type: 'Permission', id: `users-${permissionId}` },
          'User', 'Role', 'App', 'Permission'
        ],
        onQueryStarted: invalidateUserSecurity
      }),

      // ── Role Users ─────────────────────────────────────────────────────────
      getRoleUsers: builder.query<RoleUserVm[], number>({
        query: (roleId) => `/app-admin/roles/${roleId}/users`,
        providesTags: (_r, _e, roleId) => [{ type: 'RoleUser', id: roleId }]
      }),
      assignUserToRole: builder.mutation<{ userId: number; roleId: number }, { roleId: number; data: AssignUserToRoleVm }>({
        query: ({ roleId, data }) => ({
          url: `/app-admin/roles/${roleId}/users`,
          method: 'POST',
          body: data
        }),
        invalidatesTags: (_r, _e, { roleId }) => [
          { type: 'RoleUser', id: roleId },
          'User', 'Role'
        ],
        onQueryStarted: invalidateUserSecurity
      }),
      removeUserFromRole: builder.mutation<void, { roleId: number; userId: number }>({
        query: ({ roleId, userId }) => ({
          url: `/app-admin/roles/${roleId}/users/${userId}`,
          method: 'DELETE'
        }),
        invalidatesTags: (_r, _e, { roleId }) => [
          { type: 'RoleUser', id: roleId },
          'User', 'Role', 'App', 'Permission'
        ],
        onQueryStarted: invalidateUserSecurity
      }),
      toggleUserRoleActive: builder.mutation<{ appUserId: number; appRoleId: number; isActive: boolean }, { roleId: number; userId: number }>({
        query: ({ roleId, userId }) => ({
          url: `/app-admin/roles/${roleId}/users/${userId}/toggle`,
          method: 'PATCH'
        }),
        invalidatesTags: (_r, _e, { roleId }) => [
          { type: 'RoleUser', id: roleId },
          'User', 'Role', 'App', 'Permission'
        ],
        onQueryStarted: invalidateUserSecurity
      }),

      // ── Users ──────────────────────────────────────────────────────────────
      getAdminUsers: builder.query<CoreAppUserVm[], string | void>({
        query: (search) => `/app-admin/users${search ? `?search=${encodeURIComponent(search as string)}` : ''}`,
        providesTags: ['User']
      }),
      getAdminUser: builder.query<CoreAppUserVm & { createdUtc: string; lastAccessUtc?: string | null; roles: UserRoleDetailVm[] }, number>({
        query: (id) => `/app-admin/users/${id}`,
        providesTags: (_r, _e, id) => [{ type: 'User', id }]
      }),
      getUserRolesAdmin: builder.query<UserRoleDetailVm[], number>({
        query: (id) => `/app-admin/users/${id}/roles`,
        providesTags: (_r, _e, id) => [{ type: 'User', id }]
      }),
      getUserPermissions: builder.query<AdminUserPermissionVm[], number>({
        query: (id) => `/app-admin/users/${id}/permissions`,
        providesTags: (_r, _e, id) => [{ type: 'User', id: `perms-${id}` }]
      }),

      // ── Find or Create User (from AAD) ────────────────────────────────────
      findOrCreateUser: builder.mutation<CoreAppUserVm, FindOrCreateUserVm>({
        query: (body) => ({ url: '/app-admin/users/find-or-create', method: 'POST', body }),
        invalidatesTags: ['User']
      }),
      toggleUserActive: builder.mutation<{ id: number; isActive: boolean }, { id: number; isActive: boolean }>({
        query: ({ id, isActive }) => ({
          url: `/app-admin/users/${id}`,
          method: 'PATCH',
          body: { isActive }
        }),
        invalidatesTags: (_r, _e, { id }) => [{ type: 'User', id }, 'User']
      }),

      // ── Usage Logs ─────────────────────────────────────────────────────────
      getUsageLogs: builder.query<UsageLogPagedResponse, UsageLogFilters>({
        query: (filters) => {
          const params = new URLSearchParams();
          if (filters.page) params.set('page', String(filters.page));
          if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
          if (filters.appId) params.set('appId', String(filters.appId));
          if (filters.statusMin) params.set('statusMin', String(filters.statusMin));
          if (filters.statusMax) params.set('statusMax', String(filters.statusMax));
          if (filters.statusCode) params.set('statusCode', String(filters.statusCode));
          if (filters.username) params.set('username', filters.username);
          if (filters.requestUrl) params.set('requestUrl', filters.requestUrl);
          if (filters.httpMethod) params.set('httpMethod', filters.httpMethod);
          if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
          if (filters.dateTo) params.set('dateTo', filters.dateTo);
          if (filters.search) params.set('search', filters.search);
          return `/app-admin/usage-logs?${params.toString()}`;
        },
        providesTags: ['UsageLog']
      }),
      getUsageLogFilterOptions: builder.query<UsageLogFilterOptionsVm, { appId?: number; days?: number }>({
        query: ({ appId, days } = {}) => {
          const params = new URLSearchParams();
          if (appId) params.set('appId', String(appId));
          if (days) params.set('days', String(days));
          return `/app-admin/usage-logs/filter-options?${params.toString()}`;
        },
        providesTags: ['UsageLog']
      }),
      getUsageLogMetrics: builder.query<UsageLogMetricsVm, { days?: number; appId?: number }>({
        query: ({ days, appId } = {}) => {
          const params = new URLSearchParams();
          if (days) params.set('days', String(days));
          if (appId) params.set('appId', String(appId));
          return `/app-admin/usage-logs/metrics?${params.toString()}`;
        },
        providesTags: ['UsageLog']
      }),
      getEndpointDurationTrend: builder.query<DurationTrendVm, { requestUrl: string; httpMethod?: string; days?: number }>({
        query: ({ requestUrl, httpMethod, days }) => {
          const params = new URLSearchParams({ requestUrl });
          if (httpMethod) params.set('httpMethod', httpMethod);
          if (days) params.set('days', String(days));
          return `/app-admin/usage-logs/duration-trend?${params.toString()}`;
        },
        providesTags: ['UsageLog']
      }),

      // ── Sync ───────────────────────────────────────────────────────────────
      syncPrismCore: builder.mutation<SyncResultVm, void>({
        query: () => ({
          url: `/app-admin/sync-prism-core`,
          method: 'POST'
        })
      }),

      // ── Report Types ───────────────────────────────────────────────────────
      getAdminReportTypes: builder.query<ReportTypeVm[], void>({
        query: () => '/Reports/Admin/ReportTypes',
        providesTags: ['ReportType'],
      }),

      // ── Report Recipients ──────────────────────────────────────────────────
      getReportRecipients: builder.query<ReportRecipientItem[], number>({
        query: (reportTypeId) => `/Reports/Admin/ReportTypes/${reportTypeId}/Recipients`,
        providesTags: (_r, _e, reportTypeId) => [{ type: 'ReportRecipient', id: reportTypeId }],
      }),
      addReportRecipient: builder.mutation<ReportRecipientItem, { reportTypeId: number; body: AddReportRecipientVm }>({
        query: ({ reportTypeId, body }) => ({
          url: `/Reports/Admin/ReportTypes/${reportTypeId}/Recipients`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_r, _e, { reportTypeId }) => [{ type: 'ReportRecipient', id: reportTypeId }],
      }),
      updateReportRecipient: builder.mutation<ReportRecipientItem, { reportTypeId: number; appUserId: number; body: UpdateReportRecipientVm }>({
        query: ({ reportTypeId, appUserId, body }) => ({
          url: `/Reports/Admin/ReportTypes/${reportTypeId}/Recipients/${appUserId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_r, _e, { reportTypeId }) => [{ type: 'ReportRecipient', id: reportTypeId }],
      }),
      removeReportRecipient: builder.mutation<void, { reportTypeId: number; appUserId: number }>({
        query: ({ reportTypeId, appUserId }) => ({
          url: `/Reports/Admin/ReportTypes/${reportTypeId}/Recipients/${appUserId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, { reportTypeId }) => [{ type: 'ReportRecipient', id: reportTypeId }],
      }),

      // ── Data Scopes ────────────────────────────────────────────────────────
      getDataScopes: builder.query<DataScopeListItemVm[], void>({
        query: () => '/app-admin/data-scopes',
        providesTags: ['DataScope']
      }),
      getDataScope: builder.query<DataScopeDetailVm, number>({
        query: (id) => `/app-admin/data-scopes/${id}`,
        providesTags: (_r, _e, id) => [{ type: 'DataScope', id }]
      }),
      createDataScope: builder.mutation<{ id: number; scopeKey: string; displayName: string; scopeType: string; appId: number; appKey: string; appIds: number[]; appKeys: string[] }, CreateDataScopeVm>({
        query: (body) => ({ url: '/app-admin/data-scopes', method: 'POST', body }),
        invalidatesTags: ['DataScope']
      }),
      cloneDataScope: builder.mutation<{ id: number; scopeKey: string; displayName: string }, { id: number; scopeKey: string; displayName: string }>({
        query: ({ id, scopeKey, displayName }) => ({
          url: `/app-admin/data-scopes/${id}/clone`,
          method: 'POST',
          body: { scopeKey, displayName }
        }),
        invalidatesTags: ['DataScope']
      }),
      updateDataScope: builder.mutation<{ id: number; scopeKey: string; displayName: string; scopeType: string; description: string | null; isActive: boolean; appId: number; appIds: number[] }, { id: number; data: UpdateDataScopeVm }>({
        query: ({ id, data }) => ({ url: `/app-admin/data-scopes/${id}`, method: 'PUT', body: data }),
        invalidatesTags: (_r, _e, { id }) => [{ type: 'DataScope', id }, 'DataScope']
      }),
      replaceDataScopeRules: builder.mutation<{ id: number; rules: { dimension: string; operator: string; values: string[] }[] }, { id: number; data: ReplaceRulesVm }>({
        query: ({ id, data }) => ({ url: `/app-admin/data-scopes/${id}/rules`, method: 'PUT', body: data }),
        // Rule changes can move counts/leave-one-out attribution for every user assigned to this
        // scope — the bare 'DataScope' tag (no id) invalidates every provided 'DataScope' tag
        // regardless of id, which covers every `user-flow-{userId}` and `diagnostics-{userId}-{id}`
        // entry too, not just this scope's own detail cache.
        invalidatesTags: (_r, _e, { id }) => [{ type: 'DataScope', id }, 'DataScope']
      }),
      // Soft-delete only — deactivates the scope (IsActive = false); rules, app memberships, and
      // user assignments are all retained. See AdminDataScopeFunctions.DeleteDataScope.
      deleteDataScope: builder.mutation<{ id: number; isActive: boolean }, number>({
        query: (id) => ({ url: `/app-admin/data-scopes/${id}`, method: 'DELETE' }),
        invalidatesTags: (_r, _e, id) => [{ type: 'DataScope', id }, 'DataScope']
      }),
      previewDataScope: builder.mutation<PreviewResultVm, PreviewRequestVm>({
        query: (body) => ({ url: '/app-admin/data-scopes/preview', method: 'POST', body })
      }),
      getScopeDimensionLookups: builder.query<ScopeDimensionLookupsVm, { appIds?: number[]; appId?: number } | void>({
        query: (args) => {
          const appIds = args?.appIds;
          if (appIds && appIds.length > 0) {
            return `/app-admin/lookups/scope-dimensions?appIds=${appIds.join(',')}`;
          }
          const appId = args?.appId;
          return `/app-admin/lookups/scope-dimensions${appId ? `?appId=${appId}` : ''}`;
        }
      }),
      getScopeApps: builder.query<ScopeAppVm[], void>({
        query: () => '/app-admin/lookups/scope-apps',
        providesTags: ['DataScope']
      }),
      getDataScopeUsers: builder.query<DataScopeUserVm[], number>({
        query: (id) => `/app-admin/data-scopes/${id}/users`,
        providesTags: (_r, _e, id) => [{ type: 'DataScope', id: `users-${id}` }]
      }),
      assignUserToDataScope: builder.mutation<{ appUserId: number; dataScopeId: number }, { id: number; appUserId: number }>({
        query: ({ id, appUserId }) => ({ url: `/app-admin/data-scopes/${id}/users`, method: 'POST', body: { appUserId } }),
        invalidatesTags: (_r, _e, { id, appUserId }) => [
          { type: 'DataScope', id },
          { type: 'DataScope', id: `users-${id}` },
          { type: 'DataScope', id: `user-${appUserId}` },
          { type: 'DataScope', id: `user-flow-${appUserId}` },
          { type: 'DataScope', id: `diagnostics-${appUserId}-${id}` },
          'DataScope'
        ]
      }),
      bulkAssignUsersToDataScope: builder.mutation<{ dataScopeId: number; requested: number; assigned: number; skippedInvalidUserIds: number[] }, { id: number; appUserIds: number[] }>({
        query: ({ id, appUserIds }) => ({ url: `/app-admin/data-scopes/${id}/users/bulk`, method: 'POST', body: { appUserIds } }),
        invalidatesTags: (_r, _e, { id, appUserIds }) => [
          { type: 'DataScope', id },
          { type: 'DataScope', id: `users-${id}` },
          ...appUserIds.flatMap((appUserId) => [
            { type: 'DataScope' as const, id: `user-${appUserId}` },
            { type: 'DataScope' as const, id: `user-flow-${appUserId}` },
            { type: 'DataScope' as const, id: `diagnostics-${appUserId}-${id}` }
          ]),
          'DataScope'
        ]
      }),
      unassignUserFromDataScope: builder.mutation<void, { id: number; userId: number }>({
        query: ({ id, userId }) => ({ url: `/app-admin/data-scopes/${id}/users/${userId}`, method: 'DELETE' }),
        invalidatesTags: (_r, _e, { id, userId }) => [
          { type: 'DataScope', id },
          { type: 'DataScope', id: `users-${id}` },
          { type: 'DataScope', id: `user-${userId}` },
          { type: 'DataScope', id: `user-flow-${userId}` },
          { type: 'DataScope', id: `diagnostics-${userId}-${id}` },
          'DataScope'
        ]
      }),
      getUserDataScopes: builder.query<UserDataScopeVm[], number>({
        query: (userId) => `/app-admin/users/${userId}/data-scopes`,
        providesTags: (_r, _e, userId) => [{ type: 'DataScope', id: `user-${userId}` }]
      }),
      replaceUserDataScopes: builder.mutation<{ appUserId: number; dataScopeIds: number[]; skippedInvalidScopeIds: number[] }, { userId: number; dataScopeIds: number[] }>({
        query: ({ userId, dataScopeIds }) => ({ url: `/app-admin/users/${userId}/data-scopes`, method: 'PUT', body: { dataScopeIds } }),
        invalidatesTags: (_r, _e, { userId, dataScopeIds }) => [
          { type: 'DataScope', id: `user-${userId}` },
          { type: 'DataScope', id: `user-flow-${userId}` },
          ...dataScopeIds.map((dataScopeId) => ({ type: 'DataScope' as const, id: `diagnostics-${userId}-${dataScopeId}` })),
          'DataScope'
        ]
      }),
      getUserEffectiveProperties: builder.query<EffectivePropertiesVm, number>({
        query: (userId) => `/app-admin/users/${userId}/effective-properties`
      }),
      getUserDataFlow: builder.query<UserDataFlowVm, number>({
        query: (userId) => `/app-admin/users/${userId}/data-flow`,
        providesTags: (_r, _e, userId) => [{ type: 'DataScope', id: `user-flow-${userId}` }]
      }),
      // Lazy by convention on the frontend — call site should use `useLazyGetUserDataScopeDiagnosticsQuery`
      // (or `skip: true` until the panel is expanded) rather than triggering this on every page load
      // (SPEC-AUTH-005 §7.4). Tag carries both user and scope identity so a rule/app/user-assignment
      // mutation for either can invalidate exactly the affected (user, scope) diagnostics.
      getUserDataScopeDiagnostics: builder.query<UserDataScopeDiagnosticsVm, { userId: number; dataScopeId: number }>({
        query: ({ userId, dataScopeId }) => `/app-admin/users/${userId}/data-scopes/${dataScopeId}/diagnostics`,
        providesTags: (_r, _e, { userId, dataScopeId }) => [
          { type: 'DataScope', id: `diagnostics-${userId}-${dataScopeId}` },
          { type: 'DataScope', id: `user-flow-${userId}` }
        ]
      }),

      // ── Protected Field Groups (read-only) ──────────────────────────────────
      getFieldGroups: builder.query<ProtectedFieldGroupVm[], void>({
        query: () => '/app-admin/field-groups',
        providesTags: ['FieldGroup']
      }),
    })
  }
});

/**
 * The admin API service instance (a value returned by `AriApiQueryCreatorFetch`) used as the primary handle for the RTK Query `adminApi` slice — its hooks are re-exported from `AdminApi.hooks`.
 */
export const adminApi = AdminApi;

export const {
  useGetAdminAppsQuery,
  useGetAppUsersQuery,
  useGetAdminRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useCloneRoleMutation,
  useCopyPermissionsFromRoleMutation,
  useGetRolePermissionsQuery,
  useAssignPermissionToRoleMutation,
  useRemovePermissionFromRoleMutation,
  useToggleRolePermissionMutation,
  useGetAdminPermissionsQuery,
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
  useGetPermissionUsersQuery,
  useTogglePermissionUserRolesMutation,
  useGetRoleUsersQuery,
  useAssignUserToRoleMutation,
  useRemoveUserFromRoleMutation,
  useToggleUserRoleActiveMutation,
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useGetUserRolesAdminQuery,
  useGetUserPermissionsQuery,
  useFindOrCreateUserMutation,
  useToggleUserActiveMutation,
  useGetUsageLogsQuery,
  useGetUsageLogFilterOptionsQuery,
  useGetUsageLogMetricsQuery,
  useGetEndpointDurationTrendQuery,
  useSyncPrismCoreMutation,
  useGetAdminReportTypesQuery,
  useGetReportRecipientsQuery,
  useAddReportRecipientMutation,
  useUpdateReportRecipientMutation,
  useRemoveReportRecipientMutation,
  useGetDataScopesQuery,
  useGetDataScopeQuery,
  useCreateDataScopeMutation,
  useCloneDataScopeMutation,
  useUpdateDataScopeMutation,
  useReplaceDataScopeRulesMutation,
  useDeleteDataScopeMutation,
  usePreviewDataScopeMutation,
  useGetScopeDimensionLookupsQuery,
  useGetScopeAppsQuery,
  useGetDataScopeUsersQuery,
  useAssignUserToDataScopeMutation,
  useBulkAssignUsersToDataScopeMutation,
  useUnassignUserFromDataScopeMutation,
  useGetUserDataScopesQuery,
  useReplaceUserDataScopesMutation,
  useGetUserEffectivePropertiesQuery,
  useGetUserDataFlowQuery,
  useGetUserDataScopeDiagnosticsQuery,
  useLazyGetUserDataScopeDiagnosticsQuery,
  useGetFieldGroupsQuery,
} = AdminApi.hooks;
